import React, { useState, useEffect } from 'react';
import './SocialPosting.css';

export default function SocialPosting() {
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [platforms, setPlatforms] = useState({
    instagram: true,
    tiktok: false,
    facebook: false
  });
  const [accounts, setAccounts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch connected accounts on mount
  useEffect(() => {
    fetchAccounts();
    fetchPosts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await fetch('/api/social/accounts');
      const data = await response.json();
      setAccounts(data.accounts || []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await fetch('/api/social/posts');
      const data = await response.json();
      setPosts(data.posts || []);
    } catch (err) {
      console.error('Error fetching posts:', err);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePlatformToggle = (platform) => {
    setPlatforms({
      ...platforms,
      [platform]: !platforms[platform]
    });
  };

  const handleSchedulePost = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!photo) {
        setError('Please upload a photo');
        setLoading(false);
        return;
      }

      if (!caption.trim()) {
        setError('Please write a caption');
        setLoading(false);
        return;
      }

      if (!Object.values(platforms).some(v => v)) {
        setError('Please select at least one platform');
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('photo', photo);
      formData.append('caption', caption);
      formData.append('platforms', JSON.stringify(
        Object.keys(platforms).filter(p => platforms[p])
      ));

      const response = await fetch('/api/social/schedule-post', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to schedule post');
        setLoading(false);
        return;
      }

      setSuccess(data.message);
      setCaption('');
      setPhoto(null);
      setPhotoPreview(null);
      setPlatforms({ instagram: true, tiktok: false, facebook: false });

      // Refresh posts
      setTimeout(() => {
        fetchPosts();
        setSuccess('');
      }, 2000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Delete this post?')) {
      try {
        const response = await fetch(`/api/social/posts/${postId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          fetchPosts();
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  return (
    <div className="social-posting-container">
      <div className="social-header">
        <h2>Daily Social Posting</h2>
        <p className="subtitle">Upload once. We post every day to Instagram, TikTok, and Facebook.</p>
      </div>

      {/* Status Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ {success}</div>}

      <div className="social-content">
        {/* Upload Form */}
        <div className="upload-section">
          <h3>Create a Post</h3>

          {/* Photo Upload */}
          <div className="form-group">
            <label>Upload Photo or Video</label>
            <div className="upload-area">
              {photoPreview ? (
                <div className="photo-preview">
                  <img src={photoPreview} alt="Preview" />
                  <button
                    type="button"
                    className="btn-small"
                    onClick={() => {
                      setPhoto(null);
                      setPhotoPreview(null);
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <label className="upload-label">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  <div className="upload-placeholder">
                    
                    <p>Drag and drop or click to upload</p>
                    <small>JPG, PNG, GIF, MP4 • Max 50MB</small>
                  </div>
                </label>
              )}
            </div>
          </div>

          {/* Caption */}
          <div className="form-group">
            <label>Write Your Caption</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="What's the story? Tell your audience why this matters."
              rows="4"
              maxLength="2200"
              className="caption-input"
            />
            <small className="char-count">
              {caption.length} / 2,200
            </small>
          </div>

          {/* Platform Selection */}
          <div className="form-group">
            <label>Post to:</label>
            <div className="platform-checkboxes">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={platforms.instagram}
                  onChange={() => handlePlatformToggle('instagram')}
                />
                <span className="platform-name">Instagram</span>
                {accounts.find(a => a.platform === 'instagram') && (
                  <span className="connected">✓ Connected</span>
                )}
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={platforms.tiktok}
                  onChange={() => handlePlatformToggle('tiktok')}
                />
                <span className="platform-name">TikTok</span>
                {accounts.find(a => a.platform === 'tiktok') && (
                  <span className="connected">✓ Connected</span>
                )}
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={platforms.facebook}
                  onChange={() => handlePlatformToggle('facebook')}
                />
                <span className="platform-name">f Facebook</span>
                {accounts.find(a => a.platform === 'facebook') && (
                  <span className="connected">✓ Connected</span>
                )}
              </label>
            </div>
          </div>

          {/* Schedule Button */}
          <button
            onClick={handleSchedulePost}
            disabled={loading || !photo || !caption}
            className="btn-primary"
          >
            {loading ? 'Scheduling...' : '✦ Schedule Post'}
          </button>
        </div>

        {/* Scheduled Posts List */}
        <div className="posts-section">
          <h3>Scheduled Posts</h3>
          {posts.length === 0 ? (
            <p className="no-posts">
              No scheduled posts yet. Upload your first one above!
            </p>
          ) : (
            <div className="posts-list">
              {posts.map((post) => (
                <div key={post.id} className="post-card">
                  <div className="post-image">
                    <img src={post.photo_url} alt="Post" />
                  </div>
                  <div className="post-details">
                    <p className="caption">{post.caption.substring(0, 100)}...</p>
                    <div className="post-meta">
                      <span className="platform-badge">{post.platform}</span>
                      <span className={`status-badge status-${post.status}`}>
                        {post.status === 'scheduled' ? 'Scheduled' : 'Pending'}
                      </span>
                      <span className="time">
                        {new Date(post.scheduled_time).toLocaleDateString()}
                      </span>
                    </div>
                    {post.status === 'pending' && (
                      <button
                        className="btn-delete"
                        onClick={() => handleDeletePost(post.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="info-box">
        <h4>How It Works</h4>
        <ul>
          <li>Upload your photo and write a caption</li>
          <li>Select which platforms (Instagram, TikTok, Facebook)</li>
          <li>We automatically post tomorrow at 9 AM</li>
          <li>Build your audience while you focus on your clients</li>
        </ul>
      </div>
    </div>
  );
}
